import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/authContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { ATTACHMENT_TYPES, USER_ROLES } from '../lib/constants';

export default function CreatePracticePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [segnalatori, setSegnalatori] = useState([]);
  const [segnalatoreSelectorType, setSegnalatoreSelectorType] = useState('existing'); // 'existing' or 'custom'
  const [formData, setFormData] = useState({
    nome_referente: '',
    telefono: '',
    email: '',
    indirizzo: '',
    citta: '',
    tipologia: 'tetto',
    coordinate: '',
    grandezza_lotto: '',
    potenza_stimata: '',
    vincoli_presenti: '',
    note: '',
    // For admin/tecnico
    segnalatore_id: '',
    segnalatore_name: '',
  });

  const [files, setFiles] = useState({
    estratto_mappa: null,
    file_kmz: null,
    file_dwg: null,
    allegato_generico: null,
  });

  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    // Load segnalatori list if user is admin or tecnico
    if (user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.UFFICIO_TECNICO) {
      loadSegnalatori();
    }
  }, [user]);

  const loadSegnalatori = async () => {
    try {
      const response = await api.get('/api/segnalatori');
      setSegnalatori(response.data.segnalatori);
    } catch (error) {
      console.error('Failed to load segnalatori:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (type, e) => {
    if (e.target.files && e.target.files[0]) {
      setFiles({ ...files, [type]: e.target.files[0] });
    }
  };

  const removeFile = (type) => {
    setFiles({ ...files, [type]: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadProgress(0);

    try {
      // Prepare practice data
      const practiceData = {
        input_data: {
          nome_referente: formData.nome_referente,
          telefono: formData.telefono,
          email: formData.email,
          indirizzo: formData.indirizzo,
          citta: formData.citta,
          tipologia: formData.tipologia,
          coordinate: formData.coordinate,
          grandezza_lotto: formData.grandezza_lotto,
          potenza_stimata: parseFloat(formData.potenza_stimata),
          vincoli_presenti: formData.vincoli_presenti,
          note: formData.note,
        },
      };

      // Add segnalatore info if admin/tecnico
      if (user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.UFFICIO_TECNICO) {
        if (segnalatoreSelectorType === 'existing' && formData.segnalatore_id) {
          practiceData.segnalatore_id = formData.segnalatore_id;
        } else if (segnalatoreSelectorType === 'custom' && formData.segnalatore_name) {
          practiceData.segnalatore_name = formData.segnalatore_name;
        }
      }

      // Create practice
      const response = await api.post('/api/practices', practiceData);

      const practiceId = response.data.practice_id;
      setUploadProgress(20);

      // Upload files
      const fileTypes = [
        { key: 'estratto_mappa', type: ATTACHMENT_TYPES.ESTRATTO_MAPPA },
        { key: 'file_kmz', type: ATTACHMENT_TYPES.FILE_KMZ },
        { key: 'file_dwg', type: ATTACHMENT_TYPES.FILE_DWG },
        { key: 'allegato_generico', type: ATTACHMENT_TYPES.ALLEGATO_GENERICO },
      ];

      let uploaded = 0;
      for (const fileType of fileTypes) {
        if (files[fileType.key]) {
          const formData = new FormData();
          formData.append('file', files[fileType.key]);
          formData.append('practice_id', practiceId);
          formData.append('attachment_type', fileType.type);

          try {
            await api.post('/api/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            uploaded++;
            setUploadProgress(20 + (uploaded / fileTypes.length) * 80);
          } catch (uploadError) {
            console.error(`Failed to upload ${fileType.key}:`, uploadError);
            toast.error(`Errore caricamento ${files[fileType.key].name}. Riprova.`);
            // Continue with other files
          }
        }
      }

      if (uploaded > 0) {
        toast.success(`Pratica creata! ${uploaded}/${fileTypes.filter(ft => files[ft.key]).length} file caricati.`);
        navigate(`/practices/${practiceId}`);
      } else {
        toast.warning('Pratica creata ma nessun file caricato. Puoi caricarli dopo.');
        navigate(`/practices/${practiceId}`);
      }
    } catch (error) {
      console.error('Error creating practice:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la creazione della pratica');
    } finally {
      setLoading(false);
    }
  };

  const requiredFilesUploaded =
    files.estratto_mappa && files.file_kmz && files.file_dwg && files.allegato_generico;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Nuova Pratica</h1>
          <p className="text-muted-foreground">Inserisci i dati della pratica fotovoltaica</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Segnalatore Selection (Admin/Tecnico only) */}
          {(user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.UFFICIO_TECNICO) && (
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle>Segnalatore</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Label>Chi è il segnalatore di questa pratica?</Label>
                  <RadioGroup 
                    value={segnalatoreSelectorType} 
                    onValueChange={setSegnalatoreSelectorType}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="existing" id="existing" />
                      <Label htmlFor="existing" className="font-normal cursor-pointer">
                        Seleziona da lista
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="custom" id="custom" />
                      <Label htmlFor="custom" className="font-normal cursor-pointer">
                        Inserisci manualmente
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {segnalatoreSelectorType === 'existing' ? (
                  <div className="space-y-2">
                    <Label htmlFor="segnalatore_id">Segnalatore Registrato</Label>
                    <Select
                      value={formData.segnalatore_id}
                      onValueChange={(value) => setFormData({ ...formData, segnalatore_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona un segnalatore..." />
                      </SelectTrigger>
                      <SelectContent>
                        {segnalatori.map((seg) => (
                          <SelectItem key={seg.id} value={seg.id}>
                            {seg.full_name} ({seg.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {segnalatori.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Nessun segnalatore approvato trovato
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="segnalatore_name">Nome Segnalatore</Label>
                    <Input
                      id="segnalatore_name"
                      name="segnalatore_name"
                      value={formData.segnalatore_name}
                      onChange={handleChange}
                      placeholder="Es: Mario Rossi"
                    />
                    <p className="text-xs text-muted-foreground">
                      Inserisci il nome del segnalatore non registrato nel sistema
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Dati Referente */}
          <Card>
            <CardHeader>
              <CardTitle>Dati Referente Sito</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome_referente">Nome Referente *</Label>
                  <Input
                    id="nome_referente"
                    name="nome_referente"
                    value={formData.nome_referente}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefono">Telefono *</Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="citta">Città *</Label>
                  <Input
                    id="citta"
                    name="citta"
                    value={formData.citta}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="indirizzo">Indirizzo *</Label>
                <Input
                  id="indirizzo"
                  name="indirizzo"
                  value={formData.indirizzo}
                  onChange={handleChange}
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Dati Tecnici */}
          <Card>
            <CardHeader>
              <CardTitle>Dati Tecnici</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tipologia">Tipologia Impianto *</Label>
                  <select
                    id="tipologia"
                    name="tipologia"
                    value={formData.tipologia}
                    onChange={handleChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                  >
                    <option value="tetto">A Tetto</option>
                    <option value="terra">A Terra</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="potenza_stimata">Potenza Stimata (kW) *</Label>
                  <Input
                    id="potenza_stimata"
                    name="potenza_stimata"
                    type="number"
                    step="0.01"
                    value={formData.potenza_stimata}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coordinate">Coordinate *</Label>
                  <Input
                    id="coordinate"
                    name="coordinate"
                    placeholder="es: 45.4642, 9.1900"
                    value={formData.coordinate}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="grandezza_lotto">Grandezza Lotto *</Label>
                  <Input
                    id="grandezza_lotto"
                    name="grandezza_lotto"
                    placeholder="es: 200 mq"
                    value={formData.grandezza_lotto}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vincoli_presenti">Vincoli Presenti</Label>
                <Textarea
                  id="vincoli_presenti"
                  name="vincoli_presenti"
                  value={formData.vincoli_presenti}
                  onChange={handleChange}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="note">Note</Label>
                <Textarea
                  id="note"
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Documenti */}
          <Card>
            <CardHeader>
              <CardTitle>Documenti Obbligatori</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FileUploader
                label="Estratto di Mappa *"
                file={files.estratto_mappa}
                onChange={(e) => handleFileChange('estratto_mappa', e)}
                onRemove={() => removeFile('estratto_mappa')}
              />
              <FileUploader
                label="File KMZ *"
                file={files.file_kmz}
                onChange={(e) => handleFileChange('file_kmz', e)}
                onRemove={() => removeFile('file_kmz')}
              />
              <FileUploader
                label="File DWG *"
                file={files.file_dwg}
                onChange={(e) => handleFileChange('file_dwg', e)}
                onRemove={() => removeFile('file_dwg')}
              />
              <FileUploader
                label="Allegato Generico *"
                file={files.allegato_generico}
                onChange={(e) => handleFileChange('allegato_generico', e)}
                onRemove={() => removeFile('allegato_generico')}
              />

              {!requiredFilesUploaded && (
                <Alert>
                  <AlertDescription>
                    Tutti i documenti contrassegnati con * sono obbligatori
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Caricamento in corso...</span>
                    <span>{Math.round(uploadProgress)}%</span>
                  </div>
                  <Progress value={uploadProgress} />
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={loading || !requiredFilesUploaded}
              className="flex-1"
              data-testid="create-practice-submit"
            >
              {loading ? 'Creazione in corso...' : 'Crea Pratica'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              disabled={loading}
            >
              Annulla
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

const FileUploader = ({ label, file, onChange, onRemove }) => {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {!file ? (
        <div className="border-2 border-dashed rounded-lg p-4 hover:bg-muted/50 transition-colors">
          <label className="flex items-center justify-center gap-2 cursor-pointer">
            <Upload className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Seleziona file</span>
            <input type="file" onChange={onChange} className="hidden" />
          </label>
        </div>
      ) : (
        <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="text-sm font-medium">{file.name}</span>
            <span className="text-xs text-muted-foreground">
              ({(file.size / 1024).toFixed(1)} KB)
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={onRemove}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};